import React from "react";

import MoreOptionsIcon from "@mui/icons-material/MoreHoriz";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { Typography } from "@mui/material";

import { PopoverMode } from "components";

import { AssistantActionButton } from "../../styles";
import { useAssistantStepOptions } from "./hooks/useAssistantStepOptions";
import {
  AssistantCancelButton,
  AssistantMoreButton,
  AssistantProgress,
  AssistantProgressSegment,
  AssistantScenarioNote,
  AssistantScenarioReturn,
  AssistantScenarioSelection,
  AssistantStepContent,
  AssistantStepHeader,
  AssistantStepOption,
  AssistantStepOptions,
  AssistantStepWaiting,
  StyledAssistantWalkthroughSectionAssistantStepControls,
  StyledAssistantWalkthroughSectionAssistantWalkthroughLayout,
  StyledAssistantWalkthroughSectionBoxDisplay,
  StyledAssistantWalkthroughSectionTypographyWhiteSpace,
} from "./styles";
import { IAssistantWalkthroughSectionProps } from "./types";
import { getAssistantAdvanceLabel } from "./utils";

const AssistantWalkthroughSection: React.FC<IAssistantWalkthroughSectionProps> =
  ({
    title,
    step,
    index,
    stepCount,
    hasHighlight,
    isScenario = false,
    saving,
    saveFailed,
    onBack,
    onAdvance,
    onSkip,
    onRestart,
    onCancel,
    onSkipScenario,
  }) => {
    const options = useAssistantStepOptions();

    const canAdvanceManually = !step.completionEvent && !step.completionTarget;
    const advanceLabel = getAssistantAdvanceLabel(
      index + 1 === stepCount,
      isScenario
    );
    const progress = Array.from({ length: stepCount }, (_, position) => ({
      position,
      filled: position <= index,
    }));
    const waitingText = step.completionEvent
      ? "Waiting for the action to finish successfully."
      : "Select the highlighted control to continue.";

    return (
      <StyledAssistantWalkthroughSectionAssistantWalkthroughLayout
        isScenario={isScenario}
      >
        {isScenario && (
          <>
            <AssistantScenarioReturn
              fullWidth={false}
              variant="text"
              disabled={saving}
              onClick={onCancel}
            >
              ← All scenarios
            </AssistantScenarioReturn>
            <AssistantScenarioSelection variant="subtitle9">
              I’d like to learn: {title}
            </AssistantScenarioSelection>
          </>
        )}
        <AssistantStepHeader>
          <Typography variant="subtitle9">{title}</Typography>
          <StyledAssistantWalkthroughSectionBoxDisplay>
            <StyledAssistantWalkthroughSectionTypographyWhiteSpace variant="subtitle9">
              Step {index + 1} of {stepCount}
            </StyledAssistantWalkthroughSectionTypographyWhiteSpace>
            <AssistantMoreButton
              fullWidth={false}
              variant="text"
              aria-label="Walkthrough options"
              aria-haspopup="dialog"
              aria-expanded={Boolean(options.anchor)}
              aria-controls={options.anchor ? options.id : undefined}
              disabled={saving}
              onClick={options.handleOpen}
            >
              <MoreOptionsIcon fontSize="small" />
            </AssistantMoreButton>
          </StyledAssistantWalkthroughSectionBoxDisplay>
        </AssistantStepHeader>
        <AssistantProgress
          role="progressbar"
          aria-label={`${title} walkthrough progress`}
          aria-valuemin={1}
          aria-valuemax={stepCount}
          aria-valuenow={index + 1}
          aria-valuetext={`Step ${index + 1} of ${stepCount}`}
        >
          {progress.map(({ position, filled }) => (
            <AssistantProgressSegment
              key={position}
              filled={filled}
              aria-hidden
            />
          ))}
        </AssistantProgress>
        <AssistantStepContent>
          <Typography variant="h5" component="h3">
            {step.title}
          </Typography>
          <Typography variant="subtitle7">{step.instruction}</Typography>
          {!hasHighlight ? (
            <Typography variant="subtitle7" color="text.primary" role="status">
              Scroll to the target or open the relevant screen or control to
              continue. You can skip this step.
            </Typography>
          ) : (
            !canAdvanceManually && (
              <AssistantStepWaiting variant="subtitle9" role="status">
                {waitingText}
              </AssistantStepWaiting>
            )
          )}
        </AssistantStepContent>
        <StyledAssistantWalkthroughSectionAssistantStepControls
          isScenario={isScenario}
        >
          {index > 0 && (
            <AssistantActionButton
              fullWidth={false}
              round
              variant="outlined"
              disabled={saving}
              onClick={onBack}
            >
              Back
            </AssistantActionButton>
          )}
          {(canAdvanceManually || saveFailed) && (
            <AssistantActionButton
              fullWidth={false}
              round
              color="secondary"
              disabled={saving}
              onClick={onAdvance}
            >
              {saveFailed ? "Save progress" : advanceLabel}
            </AssistantActionButton>
          )}
          {isScenario && (
            <AssistantActionButton
              fullWidth={false}
              round
              variant="text"
              disabled={saving}
              onClick={onSkipScenario}
            >
              Skip scenario
            </AssistantActionButton>
          )}
          {!isScenario && (
            <AssistantCancelButton
              fullWidth={false}
              round
              variant="text"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel guide
            </AssistantCancelButton>
          )}
        </StyledAssistantWalkthroughSectionAssistantStepControls>
        {isScenario && (
          <AssistantScenarioNote variant="subtitle9">
            Your other scenarios stay available. Use “All scenarios” to switch
            tasks, or finish the walkthrough below.
          </AssistantScenarioNote>
        )}
        <AssistantStepOptions
          data-assistant-popup
          id={options.id}
          open={Boolean(options.anchor)}
          anchorEl={options.anchor}
          onClose={options.handleClose}
          mode={PopoverMode.LIGHT}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ role: "dialog", "aria-label": "Walkthrough options" }}
        >
          <AssistantStepOption
            variant="text"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={() => options.handleSelect(onRestart)}
          >
            {isScenario ? "Restart scenario" : "Restart walkthrough"}
          </AssistantStepOption>
          <AssistantStepOption
            variant="text"
            startIcon={<SkipNextIcon fontSize="small" />}
            onClick={() => options.handleSelect(onSkip)}
          >
            Skip this step
          </AssistantStepOption>
        </AssistantStepOptions>
      </StyledAssistantWalkthroughSectionAssistantWalkthroughLayout>
    );
  };

export default AssistantWalkthroughSection;
